/**
 * Bounds for promises that come from outside this bundle (roc-pvnv).
 *
 * THE SHAPE THIS EXISTS TO KILL. Two production bugs on 2026-08-10 were the
 * same defect written twice: an external resource load whose ONLY written
 * failure path was rejection. Both handled a rejection correctly. Neither
 * handled a load that never SETTLES — and that case produces no console error,
 * no `pageerror`, no failed request, and a UI that looks healthy forever.
 * `defineAsyncComponent`'s `onError` never fires (roc-mez2.3.11, see
 * lazyPanel.ts); a `finally` that clears `isLoading` never runs; a `.catch`
 * that would have scheduled a retry is never reached, so the retry machinery
 * built for the failure is defeated by it.
 *
 * The second bug was misdiagnosed as the first for most of a session, because
 * "a component that silently never appears" is what both look like from the
 * outside. That is the whole cost of leaving the shape unaudited: each instance
 * reads as a recurrence of the last one.
 *
 * THE TEST, applied to every await on a loader, SDK, script tag, socket or
 * fetch: IF THIS PROMISE NEVER SETTLES, WHAT DOES THE USER SEE? If the answer
 * is a spinner or a blank pane, it needs a bound here.
 *
 * A timeout cannot make a hung load succeed. It converts it into the error
 * state the UI already knows how to show, which is the difference between a
 * diagnosable failure and a lie.
 *
 * CHOOSING A BOUND. Every number passed to these helpers is a declaration that
 * the operation is HUNG, not a budget for a slow one. Pick something far past a
 * healthy case on a bad link, not a tight SLA — a false timeout on a genuinely
 * slow LTE link is a worse outcome than the hang, because it is reachable every
 * day rather than once in fifteen runs.
 *
 * Dependency-free, like boot-retry.ts and stale-build.ts: boot-retry imports
 * TimeoutError, and boot-retry runs before the runtime config is installed.
 */

/** A bounded operation that ran out of time, as opposed to one that failed. */
export class TimeoutError extends Error {
  constructor(what: string, ms: number) {
    super(`${what} did not complete within ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * `work`, or a rejection once `ms` has passed.
 *
 * The loser of the race is NOT cancelled — nothing here can cancel an
 * `import()` or a script tag, and pretending otherwise would be worse than
 * saying so. What the caller gets is a settled promise it can act on; the
 * abandoned work either arrives later and is ignored, or never does. Callers
 * that cache the promise must drop the cache on timeout (see cesiumLoader.ts),
 * or one hung load bricks the feature for the life of the document.
 *
 * The timer is always cleared, so a fast success does not hold a handle — and
 * under fake timers a leaked one is enough to hang a test run.
 */
export function withTimeout<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    work,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new TimeoutError(what, ms)), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Load a `<script>` and resolve once `readGlobal()` returns the global it
 * installed.
 *
 * A script element is the purest form of the shape: `onload` and `onerror`
 * BOTH fail to fire for a request that never settles, so a bare
 * `new Promise((resolve, reject) => { script.onload = ...; script.onerror = ...
 * })` has no path out at all. That is not an edge case for a CDN over LTE.
 *
 * On timeout the element is removed. It cannot un-run a script already
 * executing, but it stops a late arrival from being counted as this load and
 * keeps the document from accumulating a tag per attempt. A later call injects
 * a fresh one — and short-circuits immediately if the abandoned script did
 * eventually install its global.
 *
 * @param src        Script URL.
 * @param readGlobal Reads the global the script installs; a falsy result after
 *                   `onload` is a load that half-worked, which must reject
 *                   rather than resolve with `undefined`.
 * @param opts.what      Human name for the error message.
 * @param opts.timeoutMs When to declare the load hung.
 * @param opts.decorate  Runs on the element before it is appended, for callers
 *                       that mark their tags (e.g. `data-cesium`).
 */
export function loadScriptOnce<T>(
  src: string,
  readGlobal: () => T | undefined,
  opts: { what: string; timeoutMs: number; decorate?: (el: HTMLScriptElement) => void },
): Promise<T> {
  const { what, timeoutMs, decorate } = opts;
  const script = document.createElement('script');

  const load = new Promise<T>((resolve, reject) => {
    script.src = src;
    script.async = true;
    decorate?.(script);
    script.onload = () => {
      const loaded = readGlobal();
      if (loaded) resolve(loaded);
      else reject(new Error(`${what} loaded but installed no global`));
    };
    script.onerror = () => reject(new Error(`Failed to load ${what} from ${src}`));
    document.head.appendChild(script);
  });

  return withTimeout(load, timeoutMs, what).catch((error) => {
    script.remove();
    throw error;
  });
}

/**
 * Close `socket` if it has not opened within `timeoutMs`.
 *
 * A WebSocket that stalls mid-handshake — the TCP connection is up, the server
 * accepted it and the upgrade never completes — fires NOTHING. No `open`, no
 * `error`, no `close`, and no browser-level timeout either, since the transport
 * is healthy. Both of our sockets rebuild themselves from `onclose`, so the
 * whole fix is to produce one: `close()` on a CONNECTING socket aborts the
 * handshake and fires `close` with code 1006, which is not the 1000 either
 * reconnect path treats as deliberate.
 *
 * Listeners are added rather than assigned so this never clobbers the
 * `onopen`/`onclose` the call site sets, in either order.
 */
export function bindOpenTimeout(socket: WebSocket, timeoutMs: number, what: string): void {
  const timer = setTimeout(() => {
    if (socket.readyState !== WebSocket.CONNECTING) return;
    console.error(
      `[bounded] ${what} did not open within ${timeoutMs}ms — closing so the reconnect path runs`,
    );
    socket.close();
  }, timeoutMs);

  const clear = () => clearTimeout(timer);
  socket.addEventListener('open', clear, { once: true });
  socket.addEventListener('close', clear, { once: true });
}
